import { getStateInvalidMessage } from '../../../constants/states';

export const RECIPIENTS_COPY = {
  shippingAddress: {
    title: 'Shipping address',
    modes: {
      youProvide: {
        title: 'You provide the address',
        subtitle: 'You set it; recipients can’t change it',
      },
      claimLink: {
        title: 'Claim link',
        subtitle: 'Recipients add their own address',
      },
    },
    youProvideHint: 'Enter the address when you add the recipient — or pick one of your saved addresses',
    override: {
      button: 'Choose one address for everyone',
      hint: 'Optional — override the addresses in your list with one saved address (names kept); otherwise each recipient ships to their own address below',
      badge: 'Overrides all addresses',
      namesKept: 'Recipient names are kept',
      change: 'Change',
      remove: 'Remove',
    },
    claimLinkConfig: {
      question: 'How does the recipient get an address?',
      modes: {
        recipientDecides: 'Recipient decides',
        fromList: 'From a list',
      },
      helpers: {
        recipientDecides: 'Recipient enters their own address; any addresses you add are offered as suggestions',
        fromList: 'Recipient must choose one of the addresses you select — no manual entry',
      },
      suggested: {
        emptyTitle: 'No suggested addresses',
        title: (count: number): string => `${count} address${count === 1 ? '' : 'es'} offered`,
        helper: 'Offered as suggestions — recipient can still enter their own',
      },
      list: {
        emptyTitle: 'No addresses selected',
        title: (count: number): string => `${count} address${count === 1 ? '' : 'es'} to choose from`,
        helper: 'Recipient must pick one of these',
      },
      needsTwoBanner: 'Please select at least 2 addresses to proceed',
      addAction: 'Add',
      manageAction: 'Manage',
    },
    multiKit: {
      title: 'Claim link',
      subtitle: 'Recipients choose their gift and confirm their address when they claim',
      fixedAddressLabel: 'Fixed address',
      fixedAddressHelper: "Ships to one address you set — recipients can't change it at claim",
      chooseFixedAddress: 'Choose the shipping address',
      fixedAddressLocked: 'Recipients can’t change this address',
    },
  },
  row: {
    shipsToPrefix: 'Ships to ',
    incompleteWarning: 'Missing shipping details — not counted yet',
    missingContactWarning: 'Missing contact details — not counted yet',
    claimEntersAtClaim: 'Recipient adds address at claim',
    claimFromListPlaceholder: '—',
  },
  panel: {
    title: 'Recipients',
    searchPlaceholder: 'Search recipients',
    countOf: 'of',
    searchNoMatchPrefix: 'No recipients match “',
    searchNoMatchSuffix: '”',
    clearSearchAria: 'Clear search',
    uploadCsv: 'Upload CSV',
    addRecipient: 'Add recipient',
    bulkLocked: {
      alert: 'Your role can send to one recipient at a time. Bulk sends and CSV import need Manager access.',
      requestAccess: 'Request access',
      uploadCsvTooltip: 'CSV import creates a bulk send — needs Manager access',
      addRecipientTooltip: 'Your role can send to one recipient at a time',
    },
    empty: {
      title: 'No recipients yet',
      body: 'Add a recipient, or upload a CSV',
    },
    removeConfirm: {
      prefix: 'Remove ',
      suffix: '?',
      cancel: 'Cancel',
      remove: 'Remove',
    },
    clearAll: {
      label: 'Clear all',
      ariaLabel: 'Remove all recipients',
      confirmPrefix: 'Remove all ',
      confirmSuffix: ' recipients?',
      cancel: 'Cancel',
      confirm: 'Clear all',
      cleared: (count: number): string => `${count} recipient${count === 1 ? '' : 's'} cleared`,
    },
    removed: (count: number): string => `${count} recipient${count === 1 ? '' : 's'} removed`,
  },
  snackbar: {
    undo: 'Undo',
    dismissAria: 'Dismiss',
  },
  sidebar: {
    createTitle: 'Add recipient',
    editTitle: 'Edit recipient',
    submitCreate: 'Add recipient',
    submitEdit: 'Save changes',
    addAnother: 'Add another recipient',
    addAnotherAria: 'Keep this panel open to add another recipient',
    searchPlaceholder: 'Search contact…',
    searching: 'Searching…',
    noMatches: 'No matches',
    requiredHelper: 'Required',
    emailInvalidHelper: 'Email must be a valid email',
    zipInvalidHelper: 'Enter a valid ZIP code',
    stateInvalidHelper: getStateInvalidMessage,
    taxOptionalSuffix: '(optional)',
    taxHelper: 'Required by customs in this country for personal shipments',
    duplicate: {
      suffix: ' is already in this send',
      addAnyway: 'Add anyway',
    },
    chooseAddress: {
      title: 'Choose a saved address',
      backAria: 'Back',
      searchPlaceholder: 'Search addresses',
      noMatchPrefix: 'No addresses match “',
      noMatchSuffix: '”',
    },
    discard: {
      title: 'Discard unsaved changes?',
      bodyPrefix: 'You have unsaved changes to ',
      bodySuffix: '. Closing this panel will lose them.',
      bodyNoName: 'You have unsaved changes. Closing this panel will lose them.',
      discardButton: 'Discard changes',
      keepButton: 'Keep editing',
    },
    sections: {
      contact: 'Contact',
      shippingAddress: 'Shipping address',
      prefillAddress: 'Pre-populate an address (optional)',
    },
    fields: {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone (optional)',
      phoneRequired: 'Phone',
      phoneHelper: 'For delivery questions from the carrier',
      chooseSavedAddress: 'Choose a saved address',
      company: 'Company name (optional)',
      country: 'Country',
      address1: 'Address 1',
      addAddressLine2: 'Add address line 2',
      address2: 'Address 2 (optional)',
      city: 'City',
      state: 'State / region',
      zip: 'ZIP',
    },
  },
  settings: {
    title: 'Settings',
    timing: {
      title: 'Timing',
      putOnHold: 'Put on hold',
      putOnHoldSubtitle: 'Pause this send until a release date you choose',
      releaseDateLabel: 'Release date',
      releaseDatePlaceholder: 'Select date',
      timezone: 'Timezone',
      ok: 'OK',
      quickPicks: [
        { label: 'In 7 days', days: 7 },
        { label: 'In 30 days', days: 30 },
        { label: 'In 90 days', days: 90 },
      ],
      holdQuickPicks: [
        { label: 'Tomorrow', days: 1 },
        { label: 'In 1 week', days: 7 },
        { label: 'In 2 weeks', days: 14 },
      ],
    },
    notifications: {
      title: 'Notifications',
      sender: 'Send notifications to me',
      senderSubtitle: 'Email me delivery and claim updates — status is always available in Delivery Reports',
      recipient: 'Send tracking emails to recipients',
      recipientSubtitle: 'Recipients get shipping updates for their orders',
    },
    sendOnBehalf: {
      sectionTitle: 'Sender',
      title: 'Send on behalf of someone else',
      subtitle: "Claim and tracking emails show another sender's name",
      chooseSender: 'Choose sender',
      chooseSenderPlaceholder: 'Select sender',
      loadingSenders: 'Loading senders…',
    },
    forYourRecords: {
      title: 'For your records',
      businessReason: 'Business reason',
      businessReasonNone: 'None',
      internalNotes: 'Internal notes',
      internalNotesHelper: 'Visible to your team only — not shown to recipients',
    },
    groupShipment: {
      sectionTitle: 'Shipping',
      title: 'Group shipment',
      subtitle: 'Orders to an address-book location are flagged so the warehouse holds them for one combined shipment',
    },
    claimLink: {
      title: 'Claim link',
      expirationLabel: 'Expiration date',
      expirationPlaceholder: 'Select date & time',
      expirationHelper: 'Recipients must claim their gift by this date',
    },
  },
  addressBook: {
    title: 'Address Book',
    closeAria: 'Close',
    searchPlaceholder: 'Search',
    addAddress: 'Add address',
    noMatch: 'No addresses match your search.',
    empty: 'No addresses',
    selectAll: (count: number): string => `Select All ${count} item${count === 1 ? '' : 's'}`,
    minTwoWarning: 'Recipients need at least 2 addresses to choose from',
    cancel: 'Cancel',
    save: 'Save',
    saveError: "Something went wrong. The address wasn't saved.",
    addForm: {
      title: 'Add address',
      backAria: 'Back',
      label: 'Label',
      company: 'Company',
      country: 'Country',
      address1: 'Address line 1',
      address2: 'Address line 2',
      city: 'City',
      state: 'State / Region',
      zip: 'Zip',
      zipInvalidHelper: 'Enter a valid ZIP code',
      labelTooLongHelper: 'Label must be 70 characters or fewer',
      activeTitle: 'Active',
      activeHelper: "Inactive addresses don't appear when picking for new sends.",
    },
    editForm: {
      title: 'Edit address',
      banner: 'Edits apply everywhere this address is used — including any active sends or saved templates',
    },
    oneTime: {
      chooseTitle: 'Choose address',
      searchPlaceholder: 'Search the address book',
      enterNew: 'Enter a new address',
      sectionThisSend: 'For this send only',
      sectionTooltip: "This address won't be saved to your address book",
      sectionBook: 'From the address book',
      rowLabel: 'One-time address',
      removed: 'One-time address removed',
      undo: 'Undo',
      deleteAria: 'Remove the one-time address',
      editAria: 'Edit the one-time address',
      useSelected: 'Use selected address',
      form: {
        title: 'New address',
        editTitle: 'Edit address',
        company: 'Company',
        country: 'Country',
        address1: 'Address line 1',
        address2: 'Address line 2',
        city: 'City',
        state: 'State / Region',
        zip: 'Zip',
        zipInvalidHelper: 'Enter a valid ZIP code',
        labelTooLongHelper: 'Label must be 70 characters or fewer',
        label: 'Label',
        saveToBook: 'Also save to address book',
        saveToBookOff: 'Off — this address is used for this send only',
        saveToBookOn: "Give it a label — it'll be available for future sends",
        use: 'Use this address',
        saveAndUse: 'Save & use',
      },
    },
  },
};
